import type {
  ExperienceBlockDraft,
  ExperienceVariable,
  ResolvedVisual,
} from '@matter_extractor/shared';
import { linkUrlRefusal } from '@matter_extractor/shared';
import type { MouseEvent, ReactNode } from 'react';
import type { ReaderMode } from '../contracts';
import { VariableText } from './variable-text';

export interface LinkBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'link' }>;
  readonly visual: ResolvedVisual;
  readonly mode: ReaderMode;
  readonly resolveVariable: ((variable: ExperienceVariable) => string | null) | undefined;
  /** A preview NAMES its variables rather than resolving them (D4, D5). */
  readonly variableNames: ((variable: ExperienceVariable) => string) | undefined;
}

/**
 * Le bloc **Lien** (§9.2, issue #137) : le seul bloc qui SORT du dispositif.
 *
 * That is a difference of NATURE and not of degree, and it is the whole reason
 * this block exists beside the Bouton. A Bouton does `next-page` or
 * `next-temps`: both stay inside the experience, both are the reader's own
 * business, and `buttonBehaviour` answers what a press does. A Lien hands the
 * person to somebody else's page. So it advances nothing, it produces no
 * variable, and its behaviour is not a question the reader can answer - there
 * is nothing after the click that this application still owns.
 *
 * ## Un nouvel onglet, et pourquoi ce n'est pas un détail de confort
 *
 * `target="_blank"` with `rel="noopener noreferrer"`. A link that replaced the
 * tab would eject the person from the session she is in the middle of - §5.3
 * lets her come back through her door, but « revenir » is not a thing to ask of
 * somebody who was answering a question. `noopener` is what keeps the opened
 * page from reaching back into this one through `window.opener`, and
 * `noreferrer` is what keeps the session's own address from travelling to a
 * third party in a `Referer` header.
 *
 * ## L'adresse est REVALIDÉE ici, et pas seulement au schéma
 *
 * `experienceLinkUrlSchema` refuses `javascript:`, `data:`, `http:` and a
 * variable token, so no VERSION can carry a bad address. A DRAFT can - the
 * draft schema lets a scheme be half typed, and it says why - and the editor's
 * preview plays drafts. An `href` built from an unvalidated draft would be a
 * `javascript:` URL clickable inside `/admin`, which is the definition of the
 * defect the schema exists to prevent. So the address goes through
 * `linkUrlRefusal` again here, and a refused one is drawn as a DISABLED control
 * carrying the same label: the operator sees the block she placed, and there is
 * no `href` to click.
 *
 * ## Peint par le thème, comme un Bouton
 *
 * The same `lecteur-bouton` class and the same `data-style`: §9.4 paints the
 * buttons of a page from ONE token, and a link that painted itself would be a
 * second button style nobody chose.
 */
export function LinkBlock({
  block,
  visual,
  mode,
  resolveVariable,
  variableNames,
}: LinkBlockProps): ReactNode {
  const label = (
    <VariableText
      text={block.label}
      resolveVariable={resolveVariable}
      variableNames={variableNames}
    />
  );

  if (linkUrlRefusal(block.url) !== null) {
    return (
      <button type="button" className="lecteur-bouton" data-style={visual.buttonStyle} disabled>
        {label}
      </button>
    );
  }

  /* Hors `interactive`, le clic ne navigue pas : la scène de la Timeline montre
   * une page plutôt qu'elle ne la joue, et en `edit` la cible de sélection du
   * cadre est déjà par-dessus. `inert` couvre `edit`, pas `static`. */
  const hold = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (mode === 'interactive') return;
    event.preventDefault();
  };

  return (
    <a
      className="lecteur-bouton"
      data-style={visual.buttonStyle}
      href={block.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={hold}
    >
      {label}
    </a>
  );
}
